import React, {useState, useMemo, useRef, useEffect} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {SlideFade} from '@chakra-ui/react'

// Chakra Components
import {
    useDisclosure,
    Button,
    Box,
    Flex,
    Stack,
    Skeleton,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import EmptyCart from '../../pages/cart/partials/empty-cart'
import ProductItem from '../../components/product-item'
import {
    CartRemoveButton,
    CartEditButton,
    CartAddToWishlistButton
} from '../../pages/cart/partials/cart-secondary-button-group'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'

// Hooks
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Constants
import {
    API_ERROR_MESSAGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST
} from '@salesforce/retail-react-app/app/constants'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment,
    useProducts,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isWishlistMaxItemsReachedError} from '../../utils/wishlist-utils'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import {useProductAvailability} from '../../hooks/use-product-availability'
import CartEditQuickViewDrawer from '../cart-edit-quick-view-drawer'
import BundleProductViewModal from '../product-view-modal/bundle'

import {useCustomToast} from '../../contexts/custom-toast-context'

import {sendItemCartEvent, sendWishlistEvent} from '../../utils/gtm-utils'
import {sendBrazeCartEvent, sendBrazeWishlistEvent} from '../../utils/braze-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {useBrand} from '../../contexts/brand-context'
import { trackRemoveFromBagEvent } from '../../utils/optimizely-web-exp-utils'

const DEBOUNCE_WAIT = 750

/**
 * Build the "Product name, Colour, Size, Length" string shown in the mini-cart
 * item-removed callout. Falls back to the basket item name when the product
 * document is unavailable (offline products are omitted from the products API).
 */
const buildRemovedItemDetails = (product, fallbackName) => {
    const name = product?.name || fallbackName || ''
    const parts = name ? [name] : []
    const attributes = product?.variationAttributes
    const values = product?.variationValues
    if (Array.isArray(attributes) && values) {
        const pickValue = (candidateIds) => {
            for (const attr of attributes) {
                if (!attr?.id || !candidateIds.includes(attr.id.toLowerCase())) continue
                const raw = values[attr.id]
                if (raw == null) continue
                const match = attr.values?.find((v) => v.value === raw)
                return match?.name || raw
            }
            return null
        }
        ;[pickValue(['color', 'colour']), pickValue(['size']), pickValue(['length'])].forEach(
            (value) => value && parts.push(value)
        )
    }
    return parts.join(', ')
}

const ProductItemList = ({
    setHasFinalSaleInCart,
    isMiniCart,
    hideRemoveAction,
    hideWishlistAction,
    basketData,
    variant,
    showFewItemsLeft = false,
    onMiniCartItemsAutoRemoved
}) => {
    const {showCustomToast, removeToast} = useCustomToast()
    const authModal = useAuthModal()
    const {data: currentBasket, isLoading: isCurrentBasketLoading} = useCurrentBasket()
    const basket = basketData || currentBasket
    const isLoading = basketData ? false : isCurrentBasketLoading
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: false,
                perPricebook: true
            }
        },
        {
            enabled: Boolean(productIds),
            staleTime: 5 * 60 * 1000,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const configFinalSaleLabel = getSiteEnvVarValue(config.app, 'ENABLE_FINAL_SALE_LABEL', siteId) === 'true'

    const {data: customer} = useCurrentCustomer()
    const {customerId, isRegistered} = customer

    /***************** Product Bundles ************************/
    // Single Shopper Availability call (real-time, staleTime 0) covering bundle child variants
    // and — in the mini cart — the basket items themselves, so we never fire two separate
    // availability requests. `useProducts` above caches for 5 minutes, so its `orderable` flag
    // can be stale; this fresh call detects items that went out of stock without a page refresh
    // (it refetches whenever the mini cart re-opens).
    //
    // In the mini cart the id set is LOCKED on first capture: the first response already carries
    // every item's orderable status, so auto-removing OOS items (which shrinks basket.productItems)
    // must not shrink this list and trigger redundant availability refetches. The ref resets on
    // unmount, so each fresh mini-cart open re-captures the current basket.
    const lockedMiniCartAvailabilityIdsRef = useRef(null)
    const availabilityIds = useMemo(() => {
        const ids = new Set()
        basket?.productItems?.forEach((productItem) => {
            if (isMiniCart && productItem?.productId) ids.add(productItem.productId)
            productItem?.bundledProductItems?.forEach((childProduct) => {
                if (childProduct?.productId) ids.add(childProduct.productId)
            })
        })
        const arr = [...ids]

        if (!isMiniCart) return arr

        // Lazy-init the locked set on the first non-empty capture, then keep it stable.
        if (!lockedMiniCartAvailabilityIdsRef.current && arr.length) {
            lockedMiniCartAvailabilityIdsRef.current = arr
        }
        return lockedMiniCartAvailabilityIdsRef.current || arr
    }, [basket?.productItems, isMiniCart])

    const {data: availabilityMap} = useProductAvailability(availabilityIds, {
        enabled: availabilityIds.length > 0
    })
    // Same fresh map serves both bundle inventory calculation and mini-cart OOS detection.
    const bundleChildProductData = availabilityMap
    const miniCartAvailabilityMap = availabilityMap

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        let hasFinalSale = false

        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }
            }

            let enableFinalSaleLabel =
                configFinalSaleLabel ||
                currentProduct?.c_isEGiftCard ||
                currentProduct?.c_isGiftCard

            // Check for final sale products
            if (currentProduct?.c_isFinalSale && enableFinalSaleLabel) {
                hasFinalSale = true
            }

            updateProductsByItemId[productItem.itemId] = currentProduct
        })

        // Set the hasFinalSaleInCart state
        setHasFinalSaleInCart && setHasFinalSaleInCart(hasFinalSale)

        return updateProductsByItemId
    }, [basket, products, bundleChildProductData, setHasFinalSaleInCart])

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const addItemToBasketMutation = useShopperBasketsMutation('addItemToBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const [removingItemIds, setRemovingItemIds] = useState(new Set())
    const removedItemsCacheRef = useRef({})

    // Mini cart only: when a basket item has become unavailable / out of stock since the last
    // visit, automatically remove it from the basket and report it to the parent MiniCart so a
    // callout can be shown at the top — instead of rendering an inline "no longer available"
    // message with a Remove button.
    const autoRemovedItemIdsRef = useRef(new Set())
    useEffect(() => {
        if (!isMiniCart || !basket?.basketId || !miniCartAvailabilityMap) return

        const removals = (basket.productItems || []).filter((item) => {
            if (autoRemovedItemIdsRef.current.has(item.itemId)) return false
            // Only act on a definitive out-of-stock signal from the real-time availability API
            // (orderable === false). A missing entry means "not returned/loaded" and is left
            // alone to avoid removing valid items on a transient/empty response.
            return miniCartAvailabilityMap?.[item.productId]?.inventory?.orderable === false
        })

        if (!removals.length) return

        removals.forEach((item) => autoRemovedItemIdsRef.current.add(item.itemId))

        onMiniCartItemsAutoRemoved?.(
            removals.map((item) => ({
                itemId: item.itemId,
                details: buildRemovedItemDetails(productsByItemId?.[item.itemId], item.productName)
            }))
        )

        // Remove sequentially: basket mutations are versioned, so firing them in parallel races
        // and only some succeed — leaving out-of-stock items in the basket that reappear (with
        // their callouts) on the next open. Awaiting each keeps every removal consistent.
        ;(async () => {
            for (const item of removals) {
                try {
                    await removeItemFromBasketMutation.mutateAsync({
                        parameters: {basketId: basket.basketId, itemId: item.itemId}
                    })
                } catch {
                    // Ignore individual removal failures; the item stays and will be retried
                    // on the next mini-cart open.
                }
            }
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMiniCart, basket?.basketId, basket?.productItems, miniCartAvailabilityMap])

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const modalProps = useDisclosure()

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                !basket.shipments[0].shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        showCustomToast({
            message: formatMessage(API_ERROR_MESSAGE)
        })
    }
    /************************* Error handling ***********************/

    /**************** Wishlist ****************/
    const {data: wishlist} = useWishList()

    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const [isRedirectingAfterWishlistMove, setIsRedirectingAfterWishlistMove] = useState(false)

    const handleAddToWishlist = async (product, categoryId) => {
        try {
            if (!isRegistered) {
                authModal.setIsWishlistClick(true)
                authModal.onOpen()
                return
            }

            if (!customerId || !wishlist) {
                return
            }

            const isItemInWishlist = wishlist?.customerProductListItems?.find(
                (i) => i.productId === product?.id
            )

            if (!isItemInWishlist) {
                const createdWishlistItem = await createCustomerProductListItem.mutateAsync({
                    parameters: {
                        listId: wishlist.id,
                        customerId
                    },
                    body: {
                        // NOTE: APi does not respect quantity, it always adds 1
                        quantity: product.quantity,
                        productId: product.productId,
                        public: false,
                        priority: 1,
                        type: 'product'
                    }
                })

                const basketItem = basket?.productItems?.find(
                    (item) =>
                        item.itemId === product?.itemId ||
                        item.productId === product?.id ||
                        item.productId === product?.productId
                )
                const isLastBasketItem = (basket?.productItems?.length || 0) === 1

                // GTM Modification
                sendWishlistEvent(product, false)

                // Braze Modification - Send Braze Wishlist Event
                sendBrazeWishlistEvent(product, false, categoryId)

                if (basketItem?.itemId) {
                    await removeItemFromBasketMutation.mutateAsync({
                        parameters: {
                            basketId: basket?.basketId,
                            itemId: basketItem.itemId
                        }
                    })
                }

                const createdWishlistItemId =
                    createdWishlistItem?.id || createdWishlistItem?.itemId
                const toastId = showCustomToast({
                    message: formatMessage({
                        id: 'cart.info.added_to_wishlist',
                        defaultMessage: 'Added to wishlist'
                    }),
                    actionLabel: formatMessage({
                        id: 'cart_minicart.button.undo',
                        defaultMessage: 'Undo'
                    }),
                    onActionClick: async () => {
                        removeToast(toastId)
                        try {
                            if (basket?.basketId) {
                                await addItemToBasketMutation.mutateAsync({
                                    parameters: {basketId: basket.basketId},
                                    body: [
                                        {
                                            productId:
                                                basketItem?.productId || product?.productId,
                                            quantity:
                                                basketItem?.quantity || product?.quantity || 1,
                                            ...(basketItem?.optionItems?.length > 0 && {
                                                optionItems: basketItem.optionItems
                                            })
                                        }
                                    ]
                                })
                            }

                            if (
                                createdWishlistItemId &&
                                wishlist?.id &&
                                customerId
                            ) {
                                await deleteCustomerProductListItem.mutateAsync({
                                    parameters: {
                                        listId: wishlist.id,
                                        customerId,
                                        itemId: createdWishlistItemId
                                    }
                                })
                            }
                        } catch {
                            showError()
                        }
                    }
                })

                if (isLastBasketItem && variant === 'checkout-order-summary') {
                    setIsRedirectingAfterWishlistMove(true)
                    navigate('/cart')
                }
            } else {
                showCustomToast({
                    message: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                    actionLabel: formatMessage(TOAST_ACTION_VIEW_WISHLIST),
                    onActionClick: () => navigate('/account/wishlist')
                })
            }
        } catch (error) {
            if (await isWishlistMaxItemsReachedError(error)) {
                showCustomToast({
                    message: formatMessage(
                        {
                            id: 'cart.info.full_message',
                            defaultMessage:
                                'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                        },
                        {
                            a: (chunks) => (
                                <Link
                                    href="/account/wishlist"
                                    display="block"
                                    mt={2}
                                    textDecoration="underline"
                                >
                                    {chunks}
                                </Link>
                            )
                        }
                    )
                })
            } else {
                showError()
            }
        }
    }
    /**************** Wishlist ****************/

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    quantity: bundleQuantity
                })
            }

            if (itemsToBeUpdated.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId
                    },
                    body: itemsToBeUpdated
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    setLocalQuantity({...localQuantity, [product.itemId]: undefined})
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                }
            }
        )
    }, DEBOUNCE_WAIT)

    const handleChangeItemQuantity = async (product, value) => {
        const stockLevel = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            // Flush last call to keep ui in sync with data.
            changeItemQuantity.flush()

            // Set the selected item to the current product to the modal acts on it.
            setSelectedItem(product)

            // Show the modal.
            modalProps.onOpen()

            // Return false as 0 isn't valid section.
            return false
        }

        // Cancel any pending handlers.
        changeItemQuantity.cancel()

        // Allow use to selected values above the inventory.
        if (value > stockLevel || value === product.quantity) {
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }
    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSettled: () => {
                    // GTM Modification
                    sendItemCartEvent(productsByItemId[product.itemId], true)
                    // Braze Modification - Send Braze Cart Event
                    sendBrazeCartEvent(basket, productsByItemId[product.itemId], 'remove')

                    trackRemoveFromBagEvent({
                        sku: product.productId,
                        variationGroupId: product.c_variationGroup,
                        value: product.price,
                        category: product.c_primaryCategory,
                        source: 'cart'
                    })
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                }
            }
        )
    }

    /********* Show Cart removal toast with immediate removal and undo **********/
    const cartRemoveToast = (product) => {
        // Cache product data so we can restore it on undo and keep it visible during animation
        removedItemsCacheRef.current[product.itemId] = product

        // Trigger SlideFade exit animation immediately
        setRemovingItemIds((prev) => {
            const next = new Set(prev)
            next.add(product.itemId)
            return next
        })

        // Remove from basket immediately
        handleRemoveItem(product)

        const cleanUp = (itemId) => {
            setRemovingItemIds((prev) => {
                const next = new Set(prev)
                next.delete(itemId)
                return next
            })
            delete removedItemsCacheRef.current[itemId]
        }

        const toastId = showCustomToast({
            message: formatMessage({
                id: 'cart.info.item_removed',
                defaultMessage: 'Removed from your bag'
            }),
            actionLabel: formatMessage({
                id: 'cart_minicart.button.undo',
                defaultMessage: 'Undo'
            }),
            onActionClick: async () => {
                removeToast(toastId)
                try {
                    const cachedProduct = removedItemsCacheRef.current[product.itemId]
                    await addItemToBasketMutation.mutateAsync({
                        parameters: {basketId: basket.basketId},
                        body: [
                            {
                                productId: product.productId,
                                quantity: cachedProduct?.quantity ?? product.quantity,
                                ...(product.optionItems?.length > 0 && {
                                    optionItems: product.optionItems
                                })
                            }
                        ]
                    })
                } catch {
                    showError()
                } finally {
                    cleanUp(product.itemId)
                }
            },
            onClose: () => {
                cleanUp(product.itemId)
            }
        })
    }

    /********* Build display list (basket items + items animating out after removal) **********/
    const displayItems = useMemo(() => {
        let basketItems = [...(basket?.productItems ?? [])].reverse()
        const basketItemIds = new Set(basketItems.map((i) => i.itemId))

        // In the mini cart, out-of-stock items are auto-removed and surfaced via a top-of-cart
        // callout, so hide them from the list immediately (no inline greyed-out card). Matches the
        // auto-remove effect: only items reported as not orderable by the availability API.
        // Gate on a real basket (basketId) so a placed order (order confirmation passes
        // basketData={order}, which has orderNo not basketId) never hides just-purchased items
        // that have since gone out of stock.
        if (isMiniCart && basket?.basketId) {
            basketItems = basketItems.filter(
                (item) =>
                    miniCartAvailabilityMap?.[item.productId]?.inventory?.orderable !== false
            )
        }

        // Keep animating items visible in the list until animation/toast completes
        const animatingItems = []
        removingItemIds.forEach((itemId) => {
            const cached = removedItemsCacheRef.current[itemId]
            if (cached && !basketItemIds.has(itemId)) {
                animatingItems.push(cached)
            }
        })

        return [...basketItems, ...animatingItems]
    }, [basket?.productItems, removingItemIds, isMiniCart, miniCartAvailabilityMap])

    /********* Rendering  UI **********/
    if (isRedirectingAfterWishlistMove) {
        return null
    }

    if (isLoading || isProductsLoading) {
        const itemCount = basket?.productItems?.length || 3
        return (
            <>
                {Array.from({length: itemCount}).map((_, i) => (
                    <Skeleton
                        key={i}
                        height="180px"
                        borderRadius="md"
                        mb={{base: '10px', lg: '14px'}}
                    />
                ))}
            </>
        )
    }

    if (!isLoading && !displayItems.length) {
        // In the mini cart the empty state is owned by the parent (MiniCart renders its own
        // EmptyCart once the basket is actually empty). While an out-of-stock item is being
        // auto-removed, the basket still holds it (so the parent keeps rendering us) but it is
        // filtered out of displayItems — returning EmptyCart here would flash it for a split
        // second until the async removal completes. Return null and let the parent take over.
        if (isMiniCart) {
            return null
        }
        return <EmptyCart isRegistered={isRegistered} />
    }

    return (
        <>
            {displayItems.map((productItem, idx) => {
                const isLastItem = idx === displayItems.length - 1
                const mergedProduct = {
                    ...productItem,
                    ...(productsByItemId && productsByItemId[productItem.itemId]),
                    isProductUnavailable: !isProductsLoading
                        ? !productsByItemId?.[productItem.itemId]
                        : undefined,
                    price: productItem.price,
                    priceAfterItemDiscount: productItem.priceAfterItemDiscount,
                    priceAfterOrderDiscount: productItem.priceAfterOrderDiscount,
                    basePrice: productItem.basePrice,
                    quantity: localQuantity[productItem.itemId]
                        ? localQuantity[productItem.itemId]
                        : productItem.quantity
                }
                const shouldShowFewItemsLeft = Boolean(
                    showFewItemsLeft &&
                        mergedProduct?.c_fewItemsLeft &&
                        mergedProduct?.inventory?.orderable &&
                        !mergedProduct?.c_isEGiftCard &&
                        !mergedProduct?.c_isGiftCard
                )
                return (
                    <SlideFade
                        key={productItem.itemId}
                        in={!removingItemIds.has(productItem.itemId)}
                        unmountOnExit
                        offsetY="20px"
                    >
                    <ProductItem
                        key={productItem.itemId}
                        index={idx}
                        removeAction={
                            !hideRemoveAction && (
                                <CartRemoveButton
                                    productName={productItem?.productName}
                                    cartRemoveToast={() => cartRemoveToast(productItem)}
                                    testId={isMiniCart ? 'mini-cart-button-remove' : 'cart-remove-button'}
                                />
                            )
                        }
                        editAction={
                            !productItem?.c_isEGiftCard &&
                            !productItem?.c_isGiftCard &&
                            !isMiniCart && (
                                <CartEditButton
                                    productName={productItem?.productName}
                                    onEditClick={(product) => {
                                        setSelectedItem(product)
                                        onOpen()
                                    }}
                                />
                            )
                        }
                        wishlistAction={
                            !hideWishlistAction && (
                                <CartAddToWishlistButton onAddToWishlistClick={handleAddToWishlist} />
                            )
                        }
                        product={mergedProduct}
                        isFewItemsLeft={shouldShowFewItemsLeft}
                        onItemQuantityChange={handleChangeItemQuantity.bind(this, productItem)}
                        showLoading={
                            isCartItemLoading && selectedItem?.itemId === productItem.itemId
                        }
                        handleRemoveItem={handleRemoveItem}
                        isMiniCart={isMiniCart}
                        variant={variant}
                        isLastItem={isLastItem}
                    />
                    </SlideFade>
                )
            })}

            {isOpen && (
                <Box>
                    {!selectedItem?.bundledProductItems && selectedItem && (
                        <CartEditQuickViewDrawer
                            isOpen={isOpen}
                            onClose={onClose}
                            selectedItem={selectedItem}
                            onUpdateCart={() => {
                                setCartItemLoading(false)
                                setSelectedItem(undefined)
                            }}
                        />
                    )}
                    {selectedItem?.bundledProductItems && (
                        <BundleProductViewModal
                            isOpen={isOpen}
                            onOpen={onOpen}
                            onClose={onClose}
                            product={selectedItem}
                            updateCart={(product, quantity, childProducts) =>
                                handleUpdateBundle(product, quantity, childProducts)
                            }
                        />
                    )}
                </Box>
            )}
            <AuthModal {...authModal} />
        </>
    )
}

ProductItemList.propTypes = {
    isMiniCart: PropTypes.bool,
    hideRemoveAction: PropTypes.bool,
    hideWishlistAction: PropTypes.bool,
    basketData: PropTypes.object,
    setHasFinalSaleInCart: PropTypes.func,
    variant: PropTypes.string,
    showFewItemsLeft: PropTypes.bool,
    isLastItem: PropTypes.bool,
    onMiniCartItemsAutoRemoved: PropTypes.func
}

export default ProductItemList
